import { useState, useMemo, useEffect, useRef } from 'react';
import useSearchEngine from './useSearchEngine';
import { useFavorites } from '../context/FavoritesContext';
import { useDevMode } from '../context/DevModeContext';

/**
 * Custom hook that manages tweet filtering, sorting, and pagination
 *
 * @param {Array} allTweets - All tweets to be filtered and sorted
 * @param {Object} options - Configuration options
 * @returns {Object} State and functions for tweet management
 */
const useTwitterSearch = (allTweets, options = {}) => {
  const { tweetsPerPage = 10, initialSearchTerm = '' } = options;
  const { isFavorite } = useFavorites();
  const { isDevMode } = useDevMode();

  // Search state - initialize with URL parameter if provided
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  
  // Shuffle state
  const [isShuffled, setIsShuffled] = useState(false);
  const [shuffledTweets, setShuffledTweets] = useState([]);
  
  // Filters state
  const [filters, setFilters] = useState(() => {
    const defaultFilters = {
      likes: true,
      bookmarks: false,
      user_tweets: false,
      reddit: false,
      roam: true,
      withImages: true,
      withVideos: true,
      textOnly: true,
      over18: false,
      onlyOver18: false,
      favorites: false,
      hideSkipped: true, // HYBRID FILTER: Default to hiding skipped tweets in dev mode
    };
    
    const savedFilters = localStorage.getItem('twitterSearchFilters');
    if (savedFilters) {
      const parsed = JSON.parse(savedFilters);
      // Merge with defaults to ensure all properties exist
      return { ...defaultFilters, ...parsed };
    }
    return defaultFilters;
  });
  
  // Sort state
  const [currentSort, setCurrentSort] = useState(() => {
    const savedSort = localStorage.getItem('twitterSearchSort');
    return savedSort || 'newest';
  });
  
  // Initialize search engine
  const searchEngine = useSearchEngine(allTweets);
  const {
    searchResults,
    performSearch,
    getDisplayQuery,
    isReady: isSearchEngineReady
  } = searchEngine;
  
  // Debug: Count Reddit and Roam saves
  useEffect(() => {
    if (allTweets && allTweets.length > 0) {
      // console.log(`Found ${allTweets.filter(tweet => tweet.source === 'reddit').length} Reddit saves and ${allTweets.filter(tweet => tweet.source === 'roam').length} Roam saves out of ${allTweets.length} total tweets`);
      // if (allTweets.filter(tweet => tweet.source === 'reddit').length > 0) {
      //   console.log('Sample Reddit save:', allTweets.filter(tweet => tweet.source === 'reddit')[0]);
      // }
      // if (allTweets.filter(tweet => tweet.source === 'roam').length > 0) {
      //   console.log('Sample Roam save:', allTweets.filter(tweet => tweet.source === 'roam')[0]);
      // }
    }
  }, [allTweets]);
  
  // Persist filter preferences
  useEffect(() => {
    // console.log('Current filters:', filters);
    localStorage.setItem('twitterSearchFilters', JSON.stringify(filters));
  }, [filters]);
  
  // Persist sort preferences
  useEffect(() => {
    localStorage.setItem('twitterSearchSort', currentSort);
  }, [currentSort]);
  
  // Apply search term whenever it changes OR when search engine becomes ready
  useEffect(() => {
    if (searchTerm !== undefined && isSearchEngineReady) {
      performSearch(searchTerm);
    }
  }, [searchTerm, performSearch, isSearchEngineReady]);
  
  // Sort tweets based on the current sort option
  const getSortedTweets = (tweets, sortType) => {
    const tweetsWithEngagement = tweets.map(tweet => ({
      ...tweet,
      engagement: tweet.source === 'reddit'
        ? (tweet.ups + tweet.num_comments)
        : ((tweet.retweet_count || 0) + (tweet.reply_count || 0))
    }));

    // Helper function to get the appropriate date for sorting
    const getDateForSorting = (tweet) => {
      if (tweet.source === 'roam') {
        // For roam objects, use metadata.create_time (epoch ms)
        return new Date(tweet.metadata?.create_time || 0);
      }
      // For all other sources, use created_at
      return new Date(tweet.created_at);
    };

    switch (sortType) {
      case 'newest':
        return [...tweets].sort((a, b) => getDateForSorting(b) - getDateForSorting(a));
      case 'oldest':
        return [...tweets].sort((a, b) => getDateForSorting(a) - getDateForSorting(b));
      case 'mostEngaged':
        return tweetsWithEngagement.sort((a, b) => b.engagement - a.engagement);
      case 'leastEngaged':
        return tweetsWithEngagement.sort((a, b) => a.engagement - b.engagement);
      case 'page':
        return [...tweets].sort((a, b) => {
          // Sort by page title for Roam quotes, fall back to other criteria for non-Roam content
          const getPageTitle = (tweet) => {
            if (tweet.source === 'roam') {
              return tweet.metadata?.page_title || 'zzz_unknown'; // Put unknown pages at the end
            }
            // For non-Roam content, group by source type and then by user/subreddit
            if (tweet.source === 'reddit') {
              const subreddit = tweet.subreddit?.display_name || tweet.subreddit || 'unknown_subreddit';
              return `reddit_${subreddit}`;
            }
            if (tweet.user?.screen_name || tweet.user?.handle) {
              const username = tweet.user.screen_name || tweet.user.handle;
              return `${tweet.source}_${username}`;
            }
            return `${tweet.source}_unknown`;
          };
          
          const pageA = getPageTitle(a);
          const pageB = getPageTitle(b);
          
          // Primary sort by page/grouping
          if (pageA !== pageB) {
            return pageA.localeCompare(pageB);
          }
          
          // Secondary sort by date within the same page/group (newest first)
          return getDateForSorting(b) - getDateForSorting(a);
        });
      default:
        return tweets;
    }
  };
  
  // Apply filters to search results or all tweets
  const filteredTweets = useMemo(() => {
    // Start with search results if we have them, otherwise use all tweets
    const baseResults = searchResults
      ? searchResults.map(result => result.item)
      : allTweets;

    // Count types before filtering
    const typeCounts = baseResults.reduce((counts, tweet) => {
      counts[tweet.source] = (counts[tweet.source] || 0) + 1;
      return counts;
    }, {});
    // console.log('Types before filtering:', typeCounts);

    const roamPosts = baseResults.filter(tweet => tweet.source === 'roam');
    // console.log(`Found ${roamPosts.length} Roam posts before filtering`);
    // if (roamPosts.length > 0) {
    //   console.log('Sample Roam post:', roamPosts[0]);
    // }
    
    const filtered = baseResults.filter((tweet) => {
      // Check source matching
      const matchesSource =
        (filters.likes && tweet.source === 'like') ||
        (filters.bookmarks && tweet.source === 'bookmark') ||
        (filters.user_tweets && tweet.source === 'user_tweet') ||
        (filters.reddit && tweet.source === 'reddit') ||
        (filters.roam && tweet.source === 'roam');
      
      // Handle media detection for Twitter, Reddit, and Roam
      let hasMedia, hasImages, hasVideos;
      
      if (tweet.source === 'reddit') {
        // Reddit can have images in multiple ways:
        // 1. media_metadata for gallery/multiple images
        // 2. post_hint: "image" for single images
        // 3. domain: "i.redd.it" or similar image hosts
        // 4. URL pointing to image files
        hasImages = (tweet.media_metadata && Object.keys(tweet.media_metadata).length > 0) ||
                   tweet.post_hint === 'image' ||
                   (tweet.domain && (tweet.domain.includes('redd.it') || tweet.domain.includes('imgur'))) ||
                   (tweet.image_url && (tweet.image_url.match(/\.(jpeg|jpg|gif|png|webp)$/i) || 
                                       tweet.image_url.includes('i.redd.it') || 
                                       tweet.image_url.includes('i.imgur.com'))) ||
                   (tweet.url && (tweet.url.match(/\.(jpeg|jpg|gif|png|webp)$/i) || 
                                 tweet.url.includes('i.redd.it') || 
                                 tweet.url.includes('i.imgur.com')));
        hasVideos = tweet.is_video === true || tweet.post_hint === 'hosted:video';
        hasMedia = hasImages || hasVideos;
        
      } else if (tweet.source === 'roam') {
        // Check Roam markdown content for media
        const text = tweet.text || '';
        
        // Check for video embeds: {{[[video]]: URL}}
        hasVideos = /\{\{\[\[video\]\]:\s*https?:\/\/[^\s}]+\}\}/.test(text);
        
        // Check for images: ![](URL)
        hasImages = /!\[\]\(https?:\/\/[^)]+\)/.test(text);
        
        hasMedia = hasImages || hasVideos;
        
      } else {
        // Twitter uses medias array
        hasMedia = tweet.medias && tweet.medias.length > 0;
        hasImages = hasMedia && tweet.medias.some(media => media.type === 'photo');
        hasVideos = hasMedia && tweet.medias.some(media => media.type === 'video');
      }

      const matchesMedia =
        (filters.withImages && hasImages) ||
        (filters.withVideos && hasVideos) ||
        (filters.textOnly && !hasMedia);

      // Debug logging after media detection
      // if (tweet.source === 'reddit') {
      //   console.log(`Reddit post source match: ${matchesSource} (filter enabled: ${filters.reddit})`);
      // }
      
      // if (tweet.source === 'roam') {
      //   console.log(`Roam post source match: ${matchesSource} (filter enabled: ${filters.roam})`);
      //   console.log(`Roam post media: hasImages=${hasImages}, hasVideos=${hasVideos}, hasMedia=${hasMedia}`);
      // }

      const isOver18 = tweet.over_18 ?? false;

      const matchesAdultContent =
        (filters.onlyOver18 && isOver18) ||
        (!filters.onlyOver18 && (filters.over18 || !isOver18));

      // Check favorites filter
      const matchesFavorites = !filters.favorites || isFavorite(tweet.id);

      // HYBRID FILTER (Option 4): Skip categories handling
      // Production: Only hide tweets with 'manual' skip category
      // Dev mode: Respect the hideSkipped toggle (hides ALL skip categories when ON)
      const skipCategories = tweet.skipCategories || [];
      const hasManualSkip = skipCategories.includes('manual');
      const hasAnySkipCategory = skipCategories.length > 0;

      const matchesSkipFilter = isDevMode
        ? (!filters.hideSkipped || !hasAnySkipCategory) // Dev: toggle controls ALL categories
        : !hasManualSkip; // Production: only hide 'manual' skip category

      // Debug logging for roam posts (commented out)
      // if (tweet.source === 'roam') {
      //   console.log('Roam filtering debug:', {
      //     id: tweet.id,
      //     matchesSource,
      //     matchesMedia,
      //     matchesAdultContent,
      //     matchesFavorites,
      //     favoritesFilterEnabled: filters.favorites,
      //     isFavoriteResult: isFavorite(tweet.id)
      //   });
      // }

      // Apply media filters to both Twitter and Reddit posts
      const passesAllFilters = matchesSource && matchesMedia && matchesAdultContent && matchesFavorites && matchesSkipFilter;
      
                       
      return passesAllFilters;
    });


    return filtered;
  }, [allTweets, searchResults, filters, isFavorite, isDevMode]);
  
  // Calculate displayed tweets based on sorting, shuffling, and pagination
  const [displayedTweets, setDisplayedTweets] = useState([]);
  
  // Update displayed tweets when filters or sort changes
  useEffect(() => {
    const sortedTweets = getSortedTweets(filteredTweets, currentSort);
    const slicedTweets = sortedTweets.slice(0, tweetsPerPage);
    setDisplayedTweets(slicedTweets);
    setCurrentPage(1);
    setIsShuffled(false); // Reset shuffle state
  }, [filteredTweets, currentSort, tweetsPerPage]);
  
  // Shuffle tweets
  const handleShuffle = () => {
    const shuffled = [...filteredTweets].sort(() => 0.5 - Math.random());
    setShuffledTweets(shuffled);
    setDisplayedTweets(shuffled.slice(0, tweetsPerPage));
    setCurrentPage(1);
    setIsShuffled(true);
  };
  
  // Load more tweets
  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    const sourceList = isShuffled ? shuffledTweets : getSortedTweets(filteredTweets, currentSort);
    const nextTweets = sourceList.slice(0, nextPage * tweetsPerPage);
    setDisplayedTweets(nextTweets);
    setCurrentPage(nextPage);
  };
  
  return {
    // State
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    currentSort,
    setCurrentSort,
    displayedTweets,
    filteredTweets,
    isShuffled,
    
    // Helper functions
    getDisplayQuery: (query) => getDisplayQuery(query),
    
    // Actions
    handleShuffle,
    handleLoadMore,
    
    // Search engine access
    searchEngine
  };
};

export default useTwitterSearch;